import { Dialog, DialogContent, Typography, Box, IconButton, CircularProgress, Button } from '@mui/material';
import React, { FC } from 'react';
import img from "../../../style/images/accountimg.png";
import { useNavigate } from 'react-router-dom';
import { KYC_STATUS } from '../SharedComponent/OnboardingStatusBar';

interface BrokrageaccounProps {
    onClose: () => void;
    kycStatus?: string;
    message?: string;
    isLoading?: boolean;
    setShowBrokragePopup?: (show: boolean) => void;
    isLinkBankAvailable?: boolean;
    isAus?: boolean;  
}

const Brokrageaccoun: FC<BrokrageaccounProps> = ({
    onClose,
    kycStatus,
    message,
    isLoading = false,
    setShowBrokragePopup,
    isLinkBankAvailable = false,
    isAus = false,
}) => {
    const navigate = useNavigate();

    // Determine content based on KYC status
    const getContent = () => {
        switch (kycStatus) {
            case KYC_STATUS.PENDING:
                return {
                    title: 'Application Under Review',
                    description: message || 'We are carefully reviewing your application. This typically takes 1-2 business days. You will receive an email notification once the review is complete.',
                    showAction: false,
                };

            case KYC_STATUS.ACTION_REQUIRED:
                return {
                    title: 'Action Required',
                    description: message || 'Please review and update your application details.',
                    actionText: 'Continue Application',
                    actionRoute: '/createApplication',
                    showAction: true,
                };

            case KYC_STATUS.ON_HOLD:
                return {
                    title: 'Application On Hold',
                    description: message || 'Your application is on hold and being reviewed. We will contact you if additional information is needed.',
                    showAction: false,
                };

            case KYC_STATUS.REJECTED:
                return {
                    title: 'Application Not Approved',
                    description: message || 'Your application could not be approved at this time. Please contact support for more information.',
                    showAction: false,
                };

            case KYC_STATUS.AWAITING_BANK_LINK:
                return {
                    title: 'Application Approved!',
                    description: message || 'Congratulations! Your application has been approved. Please link your bank account to continue.',
                    actionText: 'Link Bank Account',
                    actionRoute: '/linkbankaccount',
                    showAction: true,
                };

            case KYC_STATUS.AWAITING_DEPOSIT:
                return {
                    title: 'Application Approved!',
                    description: message || 'Congratulations! Your application has been approved. Please make a deposit to activate your account.',
                    actionText: 'Make Deposit',
                    actionRoute: '/transfer',
                    showAction: true,
                };

            default:
                return {
                    title: 'Application Submitted',
                    description: message || 'Thank you for submitting your application. We will notify you once the review is complete.',
                    showAction: false,
                };
        }
    };

    const content = getContent();

    return (
        <Dialog
            open={true}
            onClose={onClose}
            aria-labelledby="dialog-title"
            PaperProps={{
                sx: {
                    borderRadius: "20px",
                    minWidth: '700px',
                    border: '1px solid #FFFFFF1A',
                },
            }}
        >
            {isLoading && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 10,
                    }}
                >
                    <CircularProgress size="40px" color="primary" />
                </Box>
            )}

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "end",
                    marginTop: "20px",
                    marginRight: "30px",
                }}
            >
                <IconButton onClick={onClose}>
                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 33C26.25 33 33 26.25 33 18C33 9.75 26.25 3 18 3C9.75 3 3 9.75 3 18C3 26.25 9.75 33 18 33Z" stroke="white" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M13.7549 22.2449L22.2449 13.7549" stroke="white" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M22.2449 22.2449L13.7549 13.7549" stroke="white" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
            </Box>

            <DialogContent
                sx={{
                    padding: "0px 40px 30px 40px",
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    alignItems: 'center'
                }}
            >
                <img src={img} height="254px" width="250px" alt="Application Status" />

                <Typography className="text-white" fontWeight="bold" fontSize="24px" textAlign="center">
                    {content.title}
                </Typography>

                <Typography textAlign="center" className="text-white" fontSize="16px">
                    {content.description}
                </Typography>

                {content.showAction && content.actionText && content.actionRoute && (
                    <>
                        <hr style={{ border: '1px solid white', width: '100%' }} />
                        <Button
                            className="btn btn-primary"
                            onClick={() => {
                                if (setShowBrokragePopup) {
                                    setShowBrokragePopup(false);
                                }
                                setTimeout(() => navigate(content.actionRoute!, {
                                    state: {
                                        canLinkBank: isLinkBankAvailable,
                                        isAus,
                                    }
                                }), 0);
                            }}
                            style={{ padding: '13px 18px' }}
                        >
                            {content.actionText}
                        </Button>
                    </>
                )}

                {!content.showAction && (
                    <Button
                        className="btn btn-primary"
                        onClick={onClose}
                        style={{ padding: '13px 18px' }}
                    >
                        Close
                    </Button>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default Brokrageaccoun;